import type { ReactNode } from 'react';
import type { DimensionValue, ViewProps } from 'react-native';

import { Box, useTheme, type Theme } from 'app/shopify/theme';

export type ThumbnailShape = 'square' | 'rounded';
export type ThumbnailImageSize = 16 | 24 | 32 | 40 | 48 | 64 | 120;

const SIZE_TOKENS = {
  16: 'sizing.general.x4',
  24: 'sizing.general.x6',
  32: 'sizing.general.x8',
  40: 'sizing.general.x10',
  48: 'sizing.general.x12',
  64: 'sizing.general.x16',
  88: 'sizing.general.x22',
  120: 'sizing.general.x30',
} as const;

interface ThumbnailFrameProps extends Pick<ViewProps, 'accessibilityLabel' | 'accessibilityRole' | 'testID'> {
  size: ThumbnailImageSize | 88 | 'free';
  shape: ThumbnailShape;
  media: boolean;
  border?: boolean;
  width?: DimensionValue;
  backgroundColor?: keyof Theme['colors'];
  children?: ReactNode;
}

function borderRadius(size: ThumbnailFrameProps['size'], shape: ThumbnailShape, media: boolean) {
  if (shape === 'rounded') return 'radius.full';
  if (size === 'free' || size === 120) return 'radius.lg';
  if (size <= (media ? 32 : 24)) return 'radius.xs';
  if (size <= (media ? 64 : 48)) return 'radius.sm';
  return 'radius.md';
}

export function ThumbnailFrame({
  size,
  shape,
  media,
  border = false,
  width = '100%',
  backgroundColor,
  accessibilityLabel,
  accessibilityRole = 'image',
  testID,
  children,
}: ThumbnailFrameProps) {
  const theme = useTheme();
  const dimension = size === 'free' ? undefined : Number(theme.tokens.sizing[SIZE_TOKENS[size]]);
  const radius = borderRadius(size, shape, media);

  return (
    <Box
      accessible={Boolean(accessibilityLabel)}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityLabel ? accessibilityRole : undefined}
      testID={testID}
      width={dimension ?? width}
      height={dimension}
      aspectRatio={1}
      flexShrink={0}
      borderRadius={radius}
      backgroundColor={backgroundColor}
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
    >
      {children}
      {border ? (
        <Box
          pointerEvents="none"
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          borderRadius={radius}
          borderWidth={theme.borderWidths['border-width.subtle']}
          borderColor="border.secondary"
        />
      ) : null}
    </Box>
  );
}
